import React, { useRef } from "react";
import BaseModel from "../models/BaseModel";
import TreeViewModel, { DragMode, DropResult } from "../models/TreeviewModel";

import Logger from "../Logger";
import TerminalStrip from "../models/TerminalStrip";
import Terminal from "../models/Terminal";
import Room from "../models/Room";
import Consumer from "../models/Consumer";
import TreeMenuIcon from "../components/NavigationTree2/TreeMenuIcon";
import { itx } from "../translationItx/itx";
import FuseConsumerReference from "../models/FuseConsumerReference";
import Fuse from "../models/Fuse";
import ELCB from "../models/ELCB";
import { EventKey } from "../eventLogger/EventKeys";

const logger = new Logger();

export default class TerminalStripTreeViewModel extends TreeViewModel {
  get typedObj(): TerminalStrip {
    return this.obj as TerminalStrip;
  }

  get isFolder(): boolean {
    return true;
  }

  getTagNeeded(): boolean {
    return true;
  }

  allowDrop(event: any): boolean {
    return true;
  }

  allowDrag(): boolean {
    return true;
  }

  async handleDrop2(mode: DragMode, obj: BaseModel): Promise<DropResult> {
    if (obj.className == "Consumer") {
      const consumer = obj as Consumer;
      if (consumer.getTerminalViaConsumer()) {
        return new DropResult(
          false,
          itx(
            "terminalStripPropertiesView.consumerAlreadyConnected",
            "Funktion ist über eine andere Funktion verkabelt",
          ),
        );
      }
      const connectable = consumer.connectWithTerminalStrip(this.typedObj);
      if (connectable) {
        this.typedObj.renumber(false);
        return new DropResult(true);
      }
    } else if (obj.className == "Room") {
      const room = obj as Room;

      let text = "";
      room.consumers.forEach((consumer) => {
        const connectable = consumer.connectWithTerminalStrip(this.typedObj);
        if (connectable) {
          if (text.length > 0) {
            text += ", ";
          }
          text += consumer.fullTag + " ";
        }
      });

      if (text.length > 0) {
        this.typedObj.name = this.typedObj.name + " " + room.name;

        this.typedObj.renumber(false);
        this.typedObj.raiseOnChange();
        return new DropResult(
          true,
          itx("terminalStripPropertiesView.connectedConsumers", "Verbraucher verbunden: ") + text,
        );
      } else {
        return new DropResult(
          false,
          itx("terminalStripPropertiesView.noConsumersInFuse", "Keine Verbraucher in Sicherung vorhanden"),
        );
      }
    } else if (obj.className == "FuseConsumerReference") {
      const fuseConsumerReference = obj as FuseConsumerReference;
      const fuse = fuseConsumerReference.referencedObject;
      if (fuse) {
        return await this.handleDrop2(mode, fuse);
      }
    } else if (obj.className == "Fuse") {
      const fuse = obj as Fuse;
      let text = "";
      fuse.consumers.forEach((consumer) => {
        const connectable = consumer.connectWithTerminalStrip(this.typedObj);
        if (connectable) {
          if (text.length > 0) {
            text += ", ";
          }
          text += consumer.fullTag + " ";
        }
      });
      if (text.length > 0) {
        this.typedObj.name = this.typedObj.name + " " + fuse.name;

        this.typedObj.renumber(false);
        return new DropResult(
          true,
          itx("terminalStripPropertiesView.connectedConsumers", "Verbraucher verbunden: ") + text,
        );
      } else {
        return new DropResult(
          false,
          itx("terminalStripPropertiesView.noConsumersInFuse", "Keine Verbraucher in Sicherung vorhanden"),
        );
      }
    } else if (obj.className == "ELCB") {
      const elcb = obj as ELCB;
      let text = "";
      elcb.fuses.sortBy("tagOrName").forEach((fuse: Fuse) => {
        fuse.consumers.forEach((consumer) => {
          // check if consumer is via Consumer they should not be connected with terminal strip
          // also check if voltage is matching
          const connectable = consumer.connectWithTerminalStrip(this.typedObj);
          if (connectable) {
            if (text.length > 0) {
              text += ", ";
            }
            text += consumer.fullTag + " ";
          }
        });
      });
      if (text.length > 0) {
        this.typedObj.name = this.typedObj.name + " " + elcb.name;

        this.typedObj.renumber(false);
        return new DropResult(
          true,
          itx("terminalStripPropertiesView.connectedConsumers", "Verbraucher verbunden: ") + text,
        );
      } else {
        return new DropResult(
          false,
          itx("terminalStripPropertiesView.noConsumersInElcb", "Keine Verbraucher im RCBO vorhanden"),
        );
      }
    }

    this.typedObj.renumber(false);

    return new DropResult(false);
  }

  get childCollection(): Array<BaseModel> | undefined {
    this.typedObj.getTerminalBlocks();

    const res = this.typedObj.terminals.sortBy("tagOrName");
    res.forEach((item) => {
      item.isLast = false;
    });

    if (res.length > 0) {
      res[res.length - 1].isLast = true;
    }

    //
    // check for conflicting Elcbs
    //
    this.typedObj.checkElcbUsage();

    return res;
  }

  get iconUrl(): string | undefined {
    if (this.typedObj) {
      return state.hostBaseUrl + "images/terminal/terminalStrip.png";
    }

    return undefined;
  }

  handleAdd() {
    try {
      const newTerminal = new Terminal({}, this.typedObj);
      newTerminal.tag = state.nameAndTagManager.generateTag(newTerminal);

      // undo Actions
      this.typedObj.terminals.add(newTerminal);
      state.eventLogger.addEvent(EventKey.ObjectCreated, {
        objectType: "Terminal",
        obj: newTerminal.id + "_" + newTerminal.tag + "_" + newTerminal.name,
      });
      state.selectionManager.selectedObject = newTerminal;
      return newTerminal;
    } catch (ex) {
      console.error(ex);
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////
  //
  // inline menu buttons
  //super.inlineButton_Properties(),
  ////////////////////////////////////////////////////////////////////////////////////////

  buildRenumberMenuItems(): any {
    return {
      title: itx("terminalStripPropertiesView.numberNew", "Neu nummerieren"),
      items: [
        {
          id: `a`,
          label: itx("TerminalStripTreeviewModel.reihenfolgeoptimieren", "Reihenfolge optimieren"),
          onClick: () => {
            this.typedObj.renumber(true);
          },
        },
        {
          id: `b`,
          label: itx("TerminalStripTreeviewModel.nurneunummerieren", "Nur neu nummerieren"),
          onClick: () => {
            this.typedObj.renumber(false);
          },
        },
      ],
    };
  }

  handleRenumber(event?: any) {
    state.contextMenuManager.show1(event, this.buildRenumberMenuItems(), []);
  }

  getButtons(): JSX.Element[] | undefined {
    return [
      super.inlineButton_Open("homePane.openTerminalStripSettings"), // no itx

      super.inlineButton_Add("ContextMenuLables.addTerminal"), // no itx

      <TreeMenuIcon
        // tooltipKey={tooltipKey}
        tooltipText={itx("terminalStripPropertiesView.numberNew", "Neu nummerieren")}
        onClick={async (ev: any) => {
          this.handleRenumber(ev);
        }}
        iconName="sort-numeric-down" // no itx
        objName={this.obj.className + this.obj.fullTag}
        key={this.obj.className + this.obj.fullTag + "renumber"}
      />,
    ];
  }

  getButtonsAsContextMenuItems(): any[] {
    return this.buttonsAsContextMenuItems().map((item) => {
      if (item.id === "btn_sort-numeric-down") {
        return { ...item, menu: this.buildRenumberMenuItems(), onClick: undefined };
      }
      return item;
    });
  }

  ////////////////////////////////////////////////////////////////////////////////////////
  //
  // context menu
  //
  ////////////////////////////////////////////////////////////////////////////////////////

  get contextMenuItems(): any {
    var subLabelPaste = undefined;

    try {
      subLabelPaste = state.contextMenuManager.clipboardData.items[0].name;
    } catch (ex) {
      logger.error(ex);
    }

    return {
      items: [
        this.baseContextMenuItems_Open(),
        this.baseContextMenuItems_Add("ContextMenuLables.addTerminal"), // no itx
        {
          key: "generateAdresses",
          label: itx("treeViewModel.renumberTerminals", "Klemmen neu nummerieren"),
          iconName: "list-ol", // no itx
          menu: this.buildRenumberMenuItems(),
        },
        this.baseContextMenuItems_Delete(),
      ],
    };
  }

  ////////////////////////////////////////////////////////////////////////////////////////
  canBePastedInteractive(copiedObjClassName: string): boolean {
    return false;
  }
  ////////////////////////////////////////////////////////////
}
